import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState} from "react";
import HandleChange from "../../../../../Helpers/HandleChange";
import {
    TABS_VISITOR,
} from "../../Model/Model";
import {useLocation, useNavigate} from "react-router-dom";
import {
    TABS_VISITOR_UPDATE
} from "../Model/Model";
import {STATUS_MODEL} from "../../../../../enums/statusEnum";
import {UpdateUserProfile} from "../../../../../reduxToolkit/Commons/CommonsApi";
import {UpdateUserStatus} from "../../../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi";
import {toast} from "react-toastify";
import routes from "../../../../../Routes/routes";
import {VISITOR_VIEWS_FROM} from '../../Guard/Model/Model';
import {isPhoneFormat, isValidLengthDocumentId} from "../../../../../Apis/functions";

/**
 * @fileoverview React hook-based component for updating visitor information.
 * Fetches user details, manages identity validation status, and allows toggling user state.
 *
 * @module VisitorUpdateApp
 * @author Karen Yunuen Pozos Arias
 * @email kpozos@ibl.mx
 * @jobTitle Full Stack Developer
 *
 * @returns {Object} Contains state and handler functions used by the Visitor Update module.
 */

export const VisitorUpdateApp = ({ setModified, item, setItem }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    let { viewOrigin  } = location?.state;
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(true);
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showDeniedModal, setShowDeniedModal] = useState(false);
    const [showActiveInactiveModal, setShowActiveInactiveModal] = useState(false);
    const [statusId, setStatusId] = useState(STATUS_MODEL.PRE_REGISTERED);
    const [isValidDocumentId, setIsValidDocumentId] = useState(true);

    /**
     * Contains modal configurations for identity validation actions.
     */
    const identityValidationModals = [
        {
            show: showApproveModal,
            setter: setShowApproveModal,
            title: 'approve_id_validation',
            description: 'you_can_leave_a_optional_comment',
            status: STATUS_MODEL.VALIDATED,
            required: false,
            successTrans:"has_been_validated_successfully"
        },
        {
            show: showDeniedModal,
            setter: setShowDeniedModal,
            title: 'decline_id_validation',
            description: 'please_provide_a_reason',
            status: STATUS_MODEL.DENIED,
            required: true,
            successTrans:"has_been_denied_successfully"
        },
    ];

    /**
     * Initializes the steps with translated labels.
     */
    useEffect(() => {
        setSteps([
            t(TABS_VISITOR_UPDATE.OVERVIEW.TRANS),
            t(TABS_VISITOR_UPDATE.IDENTITY_VERIFICATION.TRANS),
            t(TABS_VISITOR_UPDATE.INVITATION_HISTORY.TRANS),
            t(TABS_VISITOR_UPDATE.REQUEST_ACCESS.TRANS),
        ]);
    }, [t]);

    useEffect(() => {
        validateDocumentId();
    }, [item.documentId]);

    /**
     * Changes the active step in the UI.
     * @param {number} step - The index of the step to activate.
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    /**
     * Submits the updated visitor profile to the backend.
     */
    const handleUpdate = () => {
        if(!item.isValid() || !validatePhone(item.phoneNumber) || !isValidDocumentId){
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }
        setEmptyFields(false);
        dispatch(UpdateUserProfile({data:item.toRegister()}));
    };

    /**
     * Toggles visitor status between ACTIVE and PRE_REGISTERED.
     * Shows a toast notification based on the new status.
     */
    const handleUpdateStatus = () => {
        let newStatus = statusId === STATUS_MODEL.ACTIVE ? STATUS_MODEL.INACTIVE : STATUS_MODEL.ACTIVE;
        dispatch(UpdateUserStatus({ id: item.id, statusId: newStatus })).then(({ payload: { data: { success } } }) => {
            if (success) {
                setStatusId(newStatus);
                if (newStatus === STATUS_MODEL.ACTIVE) toast.success(t('has_been_activate_successfully'));
                else toast.success(t('has_been_deactivate_successfully'));
                setModified(true);
            }
        });
    };

    /**
     * Navigates back to the visitor list view.
     */
    const handleOnBack = () => {
        const urlBack = viewOrigin === VISITOR_VIEWS_FROM.VISITOR_MANAGEMENT ? routes.visitor.list : routes.visitor.listGuard;
        navigate(urlBack, {
            state: { currentTab: TABS_VISITOR.VISITORS.INDEX },
        });
    };

    /**
     * Validates whether the given phone number matches the expected format.
     *
     * @param phone the phone number to validate
     */
    const validatePhone = (phone) => {
        const isValid = isPhoneFormat(phone);
        setIsValidPhoneNumber(isValid);
        return isValid;
    };

    /**
     * handle to validate document id
     */
    const validateDocumentId = () => {
        if(item.documentId !== '' && item.documentId != null && typeof item.documentId != 'undefined'){
            setIsValidDocumentId(isValidLengthDocumentId(item.documentId));
        }else{
            setIsValidDocumentId(true);
        }
    };

    return {
        t,
        DEV,
        permission,
        steps,
        activeStep,
        setActiveStep,
        handleStepChange,
        handleChange,
        emptyFields,
        isValidPhoneNumber,
        isValidDocumentId,
        handleUpdate,
        identityValidationModals,
        setShowApproveModal,
        setShowDeniedModal,
        showActiveInactiveModal,
        setShowActiveInactiveModal,
        statusId,
        handleUpdateStatus,
        handleOnBack,
        validatePhone
    };
};
